import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { QueryKey } from '@tanstack/react-query';
import api from '../lib/api';
import type { Transaction, TransactionOverride, ReclassificationPreview, WasteAnalysisResult } from '../types';

export function useTransactions(
  caseId: string | undefined,
  filters?: {
    property_status?: string;
    waste_severity?: string;
    confidence_min?: number;
    confidence_max?: number;
    is_waste?: boolean;
    needs_review?: boolean;
  }
) {
  return useQuery<Transaction[]>({
    queryKey: ['transactions', caseId, filters],
    queryFn: async () => {
      const params: Record<string, string | number | boolean> = {};
      if (filters?.property_status) params.property_status = filters.property_status;
      if (filters?.waste_severity) params.waste_severity = filters.waste_severity;
      if (filters?.confidence_min !== undefined) params.confidence_min = filters.confidence_min;
      if (filters?.confidence_max !== undefined) params.confidence_max = filters.confidence_max;
      if (filters?.is_waste !== undefined) params.is_waste = filters.is_waste;
      if (filters?.needs_review !== undefined) params.needs_review = filters.needs_review;
      const { data } = await api.get(`/cases/${caseId}/transactions`, { params });
      return data;
    },
    enabled: !!caseId,
  });
}

export function useTransactionSummary(caseId: string | undefined) {
  return useQuery({
    queryKey: ['transactions', caseId, 'summary'],
    queryFn: async () => {
      const { data } = await api.get(`/cases/${caseId}/transactions/summary`);
      return data;
    },
    enabled: !!caseId,
  });
}

export function useTransactionRawText(transactionId: number | undefined, enabled: boolean) {
  return useQuery<{ id: number; raw_text: string | null }>({
    queryKey: ['transaction-raw-text', transactionId],
    queryFn: async () => {
      const { data } = await api.get(`/transactions/${transactionId}`);
      return data;
    },
    enabled: !!transactionId && enabled,
    staleTime: Infinity,
  });
}

export function useOverrideTransaction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ transactionId, override }: { transactionId: number; override: TransactionOverride }) => {
      const { data } = await api.put(`/transactions/${transactionId}/override`, override);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
    },
  });
}

export interface SmartSearchResult {
  transactions: Transaction[];
  search_summary: string;
  match_count: number;
  criteria?: {
    categories: string[];
    keywords: string[];
  };
}

export function useSmartSearch(caseId: string | undefined) {
  return useMutation<SmartSearchResult, Error, string>({
    mutationFn: async (query: string) => {
      const { data } = await api.post(`/cases/${caseId}/transactions/search`, { query });
      return data;
    },
  });
}

export function useReclassifyPreview(caseId: string | undefined) {
  return useMutation<ReclassificationPreview, Error, string>({
    mutationFn: async (newMarriageDate: string) => {
      const { data } = await api.post(`/cases/${caseId}/reclassify/preview`, {
        new_marriage_date: newMarriageDate,
      });
      return data;
    },
  });
}

export function useReclassifyApply(caseId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (newMarriageDate: string) => {
      const { data } = await api.post(`/cases/${caseId}/reclassify/apply`, {
        new_marriage_date: newMarriageDate,
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['cases'] });
    },
  });
}

export function useWasteAnalysis(caseId: string | undefined) {
  return useQuery<WasteAnalysisResult | null>({
    queryKey: ['waste-analysis', caseId],
    queryFn: async () => {
      const { data } = await api.get(`/cases/${caseId}/waste-analysis`);
      return data;
    },
    enabled: !!caseId,
  });
}

export function useRunWasteAnalysis(caseId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<WasteAnalysisResult, Error, string>({
    mutationFn: async (context: string) => {
      const { data } = await api.post(`/cases/${caseId}/waste-analysis`, { context });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['waste-analysis', caseId] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
    },
  });
}

export interface HiddenAssetSignalResult {
  total_flagged: number;
  confirmed_count: number;
  dismissed_count: number;
  by_category: Record<string, Transaction[]>;
  categories: string[];
}

export interface HiddenAssetScanResult {
  scanned: number;
  flagged: number;
  cleared: number;
  by_category: Record<string, number>;
}

export function useHiddenAssetSignals(caseId: string | undefined) {
  return useQuery<HiddenAssetSignalResult>({
    queryKey: ['hidden-asset-signals', caseId],
    queryFn: async () => {
      const { data } = await api.get(`/cases/${caseId}/hidden-asset-signals`);
      return data;
    },
    enabled: !!caseId,
  });
}

export function useRunHiddenAssetScan(caseId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<HiddenAssetScanResult, Error>({
    mutationFn: async () => {
      const { data } = await api.post(`/cases/${caseId}/hidden-asset-scan`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hidden-asset-signals', caseId] });
      queryClient.invalidateQueries({ queryKey: ['transactions', caseId] });
    },
  });
}

// ---- Money Trail (hidden-asset tracing) ----

export interface MoneyTrailEvidenceTxn {
  id: number;
  date: string | null;
  description: string | null;
  amount: number | null;
  bank_label: string | null;
  statement_id?: number | null;
  source_page?: number | null;
  source_file?: string | null;
}

export interface MoneyTrailLead {
  destination_key: string;
  destination_label: string;
  destination_name?: string | null;
  destination_type?: string | null;
  last4?: string | null;
  account_type?: string | null;
  is_missing_account?: boolean;
  disclosed_internal?: boolean;
  no_statements_on_file?: boolean;
  anomaly?: string | null;
  into_missing_amount?: number;
  into_missing_count?: number;
  into_missing_months?: string[];
  total_amount: number;
  returned_amount?: number;
  net_amount?: number;
  round_trip?: boolean;
  layered?: boolean;
  origin_account?: string | null;
  transfer_count: number;
  first_date: string | null;
  last_date: string | null;
  post_separation_share?: number;
  new_onset_after_separation?: boolean;
  score: number;
  tier: 'high' | 'medium' | 'watch';
  reasons: string[];
  narrative?: string;
  transaction_ids: number[];
  transactions?: MoneyTrailEvidenceTxn[];
}

export interface MoneyTrailResult {
  id: number;
  leads: MoneyTrailLead[];
  baseline: { avg_monthly_income?: number; avg_monthly_outflow?: number; months_covered?: number };
  lead_count: number;
  classified: boolean;
  separation_date: string | null;
  created_at: string | null;
}

export function useMoneyTrail(caseId: string | undefined) {
  return useQuery<MoneyTrailResult | null>({
    queryKey: ['money-trail', caseId],
    queryFn: async () => {
      const { data } = await api.get(`/cases/${caseId}/money-trail`);
      return data;
    },
    enabled: !!caseId,
  });
}

export function useRunMoneyTrail(caseId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<MoneyTrailResult, Error>({
    mutationFn: async () => {
      const { data } = await api.post(`/cases/${caseId}/money-trail/run`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['money-trail', caseId] });
    },
  });
}

export interface MerchantResearchResult {
  researched: number;
  unique_merchants?: number;
  cache_hits: number;
  ai_lookups: number;
  flagged_confirmed: number;
  flagged_suggested: number;
}

export function useResearchMerchants(caseId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<MerchantResearchResult, Error>({
    mutationFn: async () => {
      const { data } = await api.post(`/cases/${caseId}/research-merchants`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions', caseId] });
    },
  });
}

export function useSetWasteStatus(caseId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<
    { success: boolean; action: string; transaction_id: number; reviewed_at: string },
    Error,
    { transactionId: number; action: 'confirm' | 'dismiss'; reviewNote?: string }
  >({
    mutationFn: async ({ transactionId, action, reviewNote }) => {
      const { data } = await api.post(`/transactions/${transactionId}/waste-status`, {
        action,
        review_note: reviewNote || null,
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions', caseId] });
    },
  });
}

export function useHiddenAssetDecision(caseId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<
    { success: boolean; decision: string; transaction_id: number; reviewed_at: string | null },
    Error,
    { transactionId: number; decision: 'confirmed' | 'dismissed'; reviewNote?: string }
  >({
    mutationFn: async ({ transactionId, decision, reviewNote }) => {
      const { data } = await api.post(`/transactions/${transactionId}/hidden-asset-decision`, {
        decision,
        review_note: reviewNote || null,
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hidden-asset-signals', caseId] });
    },
  });
}

type StarMutationContext = { previousData: [QueryKey, Transaction[] | undefined][] } | undefined;

export function useStarTransaction(caseId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<
    { id: number; is_starred: boolean },
    Error,
    { transactionId: number; currentStarred: boolean },
    StarMutationContext
  >({
    mutationFn: async ({ transactionId }) => {
      const { data } = await api.patch(`/cases/${caseId}/transactions/${transactionId}/star`);
      return data;
    },
    onMutate: async ({ transactionId, currentStarred }) => {
      await queryClient.cancelQueries({ queryKey: ['transactions', caseId] });
      const previousData = queryClient.getQueriesData<Transaction[]>({ queryKey: ['transactions', caseId] });
      queryClient.setQueriesData<Transaction[]>(
        { queryKey: ['transactions', caseId] },
        (old) => old?.map((t) => t.id === transactionId ? { ...t, is_starred: !currentStarred } : t)
      );
      return { previousData };
    },
    onSuccess: (data) => {
      queryClient.setQueriesData<Transaction[]>(
        { queryKey: ['transactions', caseId] },
        (old) => old?.map((t) => t.id === data.id ? { ...t, is_starred: data.is_starred } : t)
      );
    },
    onError: (_err, _vars, context: StarMutationContext) => {
      if (context?.previousData) {
        for (const [queryKey, data] of context.previousData) {
          queryClient.setQueryData(queryKey, data);
        }
      }
    },
  });
}
